import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, selected }) => ({
  minWidth: 300,
  maxWidth: 350,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
}));

const DisabledOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  backdropFilter: 'blur(2px)',
});

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },
}));

const PlanCard = ({ plan, onSelect, disabled, isPromotional, selected }) => {
  const theme = useTheme();

  const getFeatures = () => {
    const baseFeatures = [
      'Perfil profissional completo',
      'Visualização de contratos',
      'Histórico de serviços',
      'Suporte prioritário'
    ];

    if (plan.name === 'Mensal') {
      return baseFeatures;
    } else if (plan.name === 'Semestral') {
      return [
        ...baseFeatures,
        'Desconto de 15%',
        'Relatórios avançados',
        'Múltiplos perfis'
      ];
    } else {
      return [
        ...baseFeatures,
        'Desconto de 30%',
        'Relatórios avançados',
        'Múltiplos perfis',
        'API de integração',
        'Suporte 24/7'
      ];
    }
  };

  return (
    <StyledCard selected={selected}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {plan.name}
          </Typography>
          {isPromotional && plan.name === 'Mensal' && (
            <Chip
              icon={<StarIcon />}
              label="Promoção"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Typography variant="h4" component="div" sx={{ mb: 2 }}>
          {isPromotional && plan.name === 'Mensal' ? (
            <>
              <Typography
                component="span"
                sx={{
                  textDecoration: 'line-through',
                  color: 'text.secondary',
                  fontSize: '1.5rem',
                  mr: 1
                }}
              >
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(plan.price)}
              </Typography>
              Grátis
            </>
          ) : (
            new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(plan.price)
          )}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          por {plan.duration} dias
        </Typography>

        <List sx={{ mb: 2, flexGrow: 1 }}>
          {getFeatures().map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={feature} />
            </ListItem>
          ))}
        </List>

        <Button
          variant={selected ? "outlined" : "contained"}
          color="primary"
          fullWidth
          onClick={() => onSelect(plan)}
          disabled={disabled}
          sx={{ mt: 'auto' }}
        >
          {disabled ? (
            <>
              <AccessTimeIcon sx={{ mr: 1 }} />
              Em breve
            </>
          ) : isPromotional && plan.name === 'Mensal' ? (
            'Ativar Plano Grátis'
          ) : (
            'Assinar Plano'
          )}
        </Button>
      </CardContent>
    </StyledCard>
  );
};

export default PlanCard; 